import { Tooltip, mergeClasses } from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { deleteTemplate, getTemplatesAdmin, updateTemplate } from "api";
import { QueryCacheConfig } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ITableColumn } from "models/ITableColumn";
import { ITemplate } from "models/ITemplate";
import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  useSharedAdminCellStyles,
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
} from "theme/sharedStyles";
import { downloadCSV, generateCSV } from "utils/generalUtils";
import { EditTemplateDialog } from "./EditTemplateDialog";
import { ViewTemplateDialog } from "./ViewTemplateDialog";

export const ManageTemplates = (): JSX.Element => {
  const adminCellStyles = useSharedAdminCellStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const [editingTemplate, setEditingTemplate] = useState<ITemplate | null>(
    null,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [viewingTemplate, setViewingTemplate] = useState<ITemplate | null>(
    null,
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<ITemplate | null>(
    null,
  );
  const [statusTogglePending, setStatusTogglePending] = useState<{
    template: ITemplate;
    checked: boolean;
  } | null>(null);
  const [visibilityTogglePending, setVisibilityTogglePending] = useState<{
    template: ITemplate;
    checked: boolean;
  } | null>(null);
  const queryClient = useQueryClient();

  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Templates",
    filterKeys: ["status", "visibility"] as const,
  });

  const { data: templatesResponse, isLoading } = useQuery(
    "templates-admin",
    getTemplatesAdmin,
    {
      refetchOnWindowFocus: false,
      staleTime: QueryCacheConfig.STALE_TIME_DEFAULT,
    },
  );

  const templates: ITemplate[] = templatesResponse?.templates || [];

  const columns: ITableColumn<ITemplate>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: ITemplate, b: ITemplate) => a.name.localeCompare(b.name),
        renderCell: (template: ITemplate) => (
          <Tooltip content={template.name} relationship="description">
            <span className={adminCellStyles.nameText} title={template.name}>
              {template.name}
            </span>
          </Tooltip>
        ),
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a: ITemplate, b: ITemplate) =>
          a.description.localeCompare(b.description),
        renderCell: (template: ITemplate) => (
          <Tooltip content={template.description} relationship="description">
            <div
              className={adminCellStyles.truncatedText}
              title={template.description}
            >
              {template.description}
            </div>
          </Tooltip>
        ),
      },
      {
        columnId: "status",
        displayValue: "Active",
        className: colStyles.medium,
        renderHeaderCell: () => "Active",
        compare: (a: ITemplate, b: ITemplate) =>
          Number(a.isActive) - Number(b.isActive),
        renderCell: (template: ITemplate) => (
          <CustomToggle
            checked={template.isActive}
            onChange={(checked: boolean) =>
              setStatusTogglePending({ template, checked })
            }
          />
        ),
      },
      {
        columnId: "showOnChatScreen",
        displayValue: "Visible on Main Chat",
        className: colStyles.medium,
        renderHeaderCell: () => "Visible on Main Chat",
        compare: (a: ITemplate, b: ITemplate) =>
          Number(a.showOnChatScreen) - Number(b.showOnChatScreen),
        renderCell: (template: ITemplate) => (
          <CustomToggle
            checked={template.showOnChatScreen}
            onChange={(checked: boolean) =>
              setVisibilityTogglePending({ template, checked })
            }
          />
        ),
      },
      {
        columnId: "recommendedLLM",
        displayValue: "LLM",
        className: colStyles.normal,
        renderHeaderCell: () => "LLM",
        compare: (a: ITemplate, b: ITemplate) =>
          a.recommendedLLM.localeCompare(b.recommendedLLM),
        renderCell: (template: ITemplate) => (
          <Tooltip content={template.recommendedLLM} relationship="description">
            <span title={template.recommendedLLM}>
              {template.recommendedLLM}
            </span>
          </Tooltip>
        ),
      },
    ],
    [adminCellStyles.nameText, adminCellStyles.truncatedText, colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<ITemplate>({
    cacheKey: "Templates",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const searchFields = useMemo(
    () => ["name", "description", "recommendedLLM"] as (keyof ITemplate)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<ITemplate>({
    items: templates,
    searchFields,
  });

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.status) {
      result = result.filter((t) =>
        filters.status === "active" ? t.isActive : !t.isActive,
      );
    }
    if (filters.visibility) {
      result = result.filter((t) =>
        filters.visibility === "visible"
          ? t.showOnChatScreen
          : !t.showOnChatScreen,
      );
    }
    return result;
  }, [filteredItems, filters.status, filters.visibility]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<ITemplate>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewTemplate = (template: ITemplate) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setViewingTemplate(template);
    setIsViewDialogOpen(true);
  };

  const handleEditTemplate = (template: ITemplate) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setEditingTemplate(template);
    setIsEditDialogOpen(true);
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setIsEditDialogOpen(true);
  };

  const handleDeleteTemplate = (template: ITemplate) => {
    setDeletingTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTemplate) return;
    setIsSaving(true);
    try {
      await deleteTemplate(deletingTemplate.id);
      await Promise.all([
        queryClient.invalidateQueries("templates"),
        queryClient.invalidateQueries("templates-admin"),
        queryClient.invalidateQueries("chat-screen-templates"),
      ]);
    } catch (error) {
      console.error("Failed to delete template:", error);
    } finally {
      setIsSaving(false);
      setIsDeleteDialogOpen(false);
      setDeletingTemplate(null);
    }
  };

  const handleToggleStatusConfirmed = useCallback(async () => {
    if (!statusTogglePending) return;
    const { template: tpl, checked } = statusTogglePending;
    setStatusTogglePending(null);

    queryClient.setQueryData("templates-admin", (old: any) => {
      if (!old?.templates) return old;
      return {
        ...old,
        templates: old.templates.map((t: ITemplate) =>
          t.id === tpl.id ? { ...t, isActive: checked } : t,
        ),
      };
    });

    try {
      await updateTemplate(tpl.id, { isActive: checked });
      await Promise.all([
        queryClient.invalidateQueries("templates"),
        queryClient.invalidateQueries("templates-admin"),
        queryClient.invalidateQueries("chat-screen-templates"),
      ]);
    } catch (error) {
      console.error("Failed to update template status:", error);
      queryClient.setQueryData("templates-admin", (old: any) => {
        if (!old?.templates) return old;
        return {
          ...old,
          templates: old.templates.map((t: ITemplate) =>
            t.id === tpl.id ? { ...t, isActive: !checked } : t,
          ),
        };
      });
    }
  }, [statusTogglePending, queryClient]);

  const handleToggleVisibilityConfirmed = useCallback(async () => {
    if (!visibilityTogglePending) return;
    const { template: tpl, checked } = visibilityTogglePending;
    setVisibilityTogglePending(null);

    queryClient.setQueryData("templates-admin", (old: any) => {
      if (!old?.templates) return old;
      return {
        ...old,
        templates: old.templates.map((t: ITemplate) =>
          t.id === tpl.id ? { ...t, showOnChatScreen: checked } : t,
        ),
      };
    });

    try {
      await updateTemplate(tpl.id, { showOnChatScreen: checked });
      await Promise.all([
        queryClient.invalidateQueries("templates"),
        queryClient.invalidateQueries("templates-admin"),
        queryClient.invalidateQueries("chat-screen-templates"),
      ]);
    } catch (error) {
      console.error("Failed to update template visibility:", error);
      queryClient.setQueryData("templates-admin", (old: any) => {
        if (!old?.templates) return old;
        return {
          ...old,
          templates: old.templates.map((t: ITemplate) =>
            t.id === tpl.id ? { ...t, showOnChatScreen: !checked } : t,
          ),
        };
      });
    }
  }, [visibilityTogglePending, queryClient]);

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingTemplate(null);
    triggerRef.current?.focus();
    triggerRef.current = null;
  };

  const handleEditSuccess = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries("templates"),
        queryClient.invalidateQueries("templates-admin"),
        queryClient.invalidateQueries("chat-screen-templates"),
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Title", accessor: (t) => t.name },
      { header: "Description", accessor: (t) => t.overview || "" },
      { header: "LLM", accessor: (t) => t.recommendedLLM || "" },
    ]);
    downloadCSV(csv, "templates.csv");
  }, [sortedItems]);

  const rowActions: AdminRowAction<ITemplate>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewTemplate,
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditTemplate,
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteTemplate,
      },
    ],
    [],
  );

  const sidebarItem = useMemo(() => findSidebarItem("templates"), []);

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {sidebarItem?.label || "Templates"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleAddTemplate}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search templates..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search templates..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              aria-label="All Status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.visibility}
              onChange={(e) => setFilter("visibility", e.target.value)}
              aria-label="All Visibility"
            >
              <option value="">All Visibility</option>
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<ITemplate>
        title="Templates"
        items={templates}
        columns={columns}
        isLoading={isLoading || isSaving}
        headerActions={[]}
        searchPlaceholder="Search templates..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(template) => template.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          internalSearchQuery || filters.status
            ? "No templates match the current filters."
            : 'No templates found. Use "Add New" to create one.'
        }
        highlightedRowId={
          statusTogglePending?.template.id ||
          visibilityTogglePending?.template.id ||
          undefined
        }
      />

      {isEditDialogOpen && (
        <EditTemplateDialog
          template={editingTemplate}
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}

      {viewingTemplate && (
        <ViewTemplateDialog
          template={viewingTemplate}
          isOpen={isViewDialogOpen}
          onClose={() => {
            setIsViewDialogOpen(false);
            setViewingTemplate(null);
            triggerRef.current?.focus();
            triggerRef.current = null;
          }}
        />
      )}

      {visibilityTogglePending && (
        <ActionDialog
          isVisible={!!visibilityTogglePending}
          title="Update Template Visibility"
          message={`Are you sure you want to ${visibilityTogglePending.checked ? "show" : "hide"} "${visibilityTogglePending.template.name}" on the main chat?`}
          confirmationMessage="Confirm"
          dismissMessage="Cancel"
          onConfirmation={handleToggleVisibilityConfirmed}
          onDismiss={() => setVisibilityTogglePending(null)}
        />
      )}

      {statusTogglePending && (
        <ActionDialog
          isVisible={!!statusTogglePending}
          title="Update Template Status"
          message={`Are you sure you want to ${statusTogglePending.checked ? "activate" : "deactivate"} "${statusTogglePending.template.name}"?`}
          confirmationMessage="Confirm"
          dismissMessage="Cancel"
          onConfirmation={handleToggleStatusConfirmed}
          onDismiss={() => setStatusTogglePending(null)}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Template"
        message={`Are you sure you want to permanently delete "${deletingTemplate?.name}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingTemplate(null);
        }}
      />
    </>
  );
};
